import { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle2, ArrowRight, type LucideIcon } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Meta } from '@/components/seo/Meta';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FadeIn } from '@/components/motion/FadeIn';
import { TextSplit, MaskReveal } from '@/components/motion/Reveal';
import { Skeleton } from '@/components/ui/Skeleton';
import { VideoPricingBlock } from '@/components/services/VideoPricingBlock';
import { useServices } from '@/lib/cms';
import type { Service } from '@/lib/cms-types';

const FALLBACK: Service[] = [
  { id: 's1', slug: 'custom-software', title: 'Custom Software Development', summary: 'Full-stack bespoke products, from sketch to production.', description_md: 'We design and engineer bespoke products — web apps, internal tools, customer-facing platforms. You bring the problem; we own the architecture, code, and infrastructure.', icon: 'Code2', deliverables: ['Discovery & architecture', 'Full-stack implementation', 'Automated tests', 'CI/CD', 'Observability & on-call'], tech_strip: ['TypeScript', 'React', 'Node', 'Supabase', 'Postgres'], cta_label: 'Start a project', cta_href: '/contact?topic=new-project', display_order: 1, is_featured: true, meta_title: null, meta_description: null, topic_slug: 'new-project', pricing: null },
  { id: 's2', slug: 'web', title: 'Web Development', summary: 'Marketing sites, web apps, dashboards — fast and accessible.', description_md: 'High-performance web experiences. Marketing sites, customer portals, internal dashboards.', icon: 'Globe', deliverables: ['Lighthouse 100s', 'WCAG AA', 'SSR + ISR', 'CMS integration'], tech_strip: ['React', 'Vite', 'Tailwind', 'Cloudflare'], cta_label: 'Start a project', cta_href: '/contact?topic=new-project', display_order: 2, is_featured: true, meta_title: null, meta_description: null, topic_slug: 'new-project', pricing: null },
  { id: 's3', slug: 'mobile', title: 'Mobile App Development', summary: 'iOS, Android, React Native, Flutter.', description_md: 'Native and cross-platform mobile apps with thoughtful UX, offline-first design, and CI-shipped builds.', icon: 'Smartphone', deliverables: ['iOS + Android', 'Offline-first sync', 'Push notifications', 'App Store + Play Store submission'], tech_strip: ['React Native', 'Flutter', 'Swift', 'Kotlin'], cta_label: 'Start a project', cta_href: '/contact?topic=new-project', display_order: 3, is_featured: false, meta_title: null, meta_description: null, topic_slug: 'new-project', pricing: null },
  { id: 's4', slug: 'ai', title: 'AI Solutions', summary: 'LLM-powered features, RAG, agents, classification, vision.', description_md: 'Practical AI: retrieval-augmented search, agents, classification, vision. We ship features with guardrails, eval harnesses, and observability.', icon: 'Brain', deliverables: ['LLM feature integration', 'RAG pipelines', 'Evaluation harnesses', 'Latency + cost budgets'], tech_strip: ['OpenAI', 'Anthropic', 'pgvector', 'LangSmith'], cta_label: 'Start a project', cta_href: '/contact?topic=new-project', display_order: 4, is_featured: true, meta_title: null, meta_description: null, topic_slug: 'new-project', pricing: null },
  { id: 's5', slug: 'cloud', title: 'Cloud Infrastructure', summary: 'AWS, Cloudflare, Supabase — designed to scale.', description_md: 'Production-grade cloud architecture. Multi-region, observable, cost-aware.', icon: 'Cloud', deliverables: ['IaC with Terraform', 'Multi-region failover', 'Observability stack', 'FinOps dashboards'], tech_strip: ['AWS', 'Cloudflare', 'Supabase', 'Terraform'], cta_label: 'Start a project', cta_href: '/contact?topic=new-project', display_order: 5, is_featured: false, meta_title: null, meta_description: null, topic_slug: 'new-project', pricing: null },
  { id: 's6', slug: 'design', title: 'UI/UX Design', summary: 'Research, wireframes, design systems, prototypes.', description_md: 'End-to-end product design — research, information architecture, design systems, prototypes. We work in Figma and ship ready-to-build specs.', icon: 'PenTool', deliverables: ['User research', 'Wireframes', 'Design system', 'Hi-fi prototypes'], tech_strip: ['Figma', 'Maze', 'Notion'], cta_label: 'Start a project', cta_href: '/contact?topic=new-project', display_order: 6, is_featured: true, meta_title: null, meta_description: null, topic_slug: 'new-project', pricing: null },
  { id: 's7', slug: 'api', title: 'API Development', summary: 'REST, GraphQL, tRPC, webhooks.', description_md: 'APIs designed for the people who will consume them. Versioned, documented, and stable.', icon: 'Webhook', deliverables: ['OpenAPI spec', 'Versioning policy', 'SDK generation', 'Webhook signing'], tech_strip: ['REST', 'GraphQL', 'tRPC', 'OpenAPI'], cta_label: 'Start a project', cta_href: '/contact?topic=new-project', display_order: 7, is_featured: false, meta_title: null, meta_description: null, topic_slug: 'new-project', pricing: null },
  { id: 's8', slug: 'digital-transformation', title: 'Digital Transformation', summary: 'Legacy modernisation, data migration.', description_md: 'We help teams move off legacy stacks without freezing the roadmap. Phased migrations, parallel run, and clear rollback plans.', icon: 'Workflow', deliverables: ['Legacy audit', 'Migration plan', 'Parallel run', 'Cutover playbook'], tech_strip: ['AWS', 'Terraform', 'Postgres'], cta_label: 'Start a project', cta_href: '/contact?topic=new-project', display_order: 8, is_featured: false, meta_title: null, meta_description: null, topic_slug: 'new-project', pricing: null },
  { id: 's9', slug: 'automation', title: 'Automation', summary: 'Workflow automation, RPA-lite.', description_md: 'Replace copy-paste work with reliable automation. Audit, integrate, monitor.', icon: 'Bot', deliverables: ['Process audit', 'Integrations', 'Monitoring', 'Runbooks'], tech_strip: ['n8n', 'Make', 'Python'], cta_label: 'Start a project', cta_href: '/contact?topic=new-project', display_order: 9, is_featured: false, meta_title: null, meta_description: null, topic_slug: 'new-project', pricing: null },
  { id: 's10', slug: 'it-consulting', title: 'IT Consulting', summary: 'Architecture review, security audits.', description_md: 'Senior engineering perspective when you need a second opinion. Architecture reviews, security audits, vendor selection.', icon: 'Compass', deliverables: ['Architecture review', 'Security audit', 'Vendor selection', 'Roadmap'], tech_strip: [], cta_label: 'Book a session', cta_href: '/contact?topic=general', display_order: 10, is_featured: false, meta_title: null, meta_description: null, topic_slug: 'general', pricing: null },
  { id: 's11', slug: 'maintenance', title: 'Maintenance & Support', summary: 'SLAs, on-call, observability.', description_md: 'Predictable support for products in production. Clear SLAs, on-call rotations, observability baked in.', icon: 'ShieldCheck', deliverables: ['Defined SLA', 'On-call rotation', 'Incident response', 'Monthly health report'], tech_strip: ['Sentry', 'PagerDuty', 'Grafana'], cta_label: 'Talk to support', cta_href: '/contact?topic=general', display_order: 11, is_featured: false, meta_title: null, meta_description: null, topic_slug: 'general', pricing: null },
  { id: 's12', slug: 'devops', title: 'DevOps', summary: 'CI/CD, IaC, Kubernetes, observability.', description_md: 'Ship faster and safer. Reproducible builds, automated tests, and infrastructure as code.', icon: 'ServerCog', deliverables: ['CI/CD pipelines', 'IaC with Terraform', 'Container orchestration', 'Observability'], tech_strip: ['GitHub Actions', 'Terraform', 'Kubernetes', 'Grafana'], cta_label: 'Start a project', cta_href: '/contact?topic=new-project', display_order: 12, is_featured: false, meta_title: null, meta_description: null, topic_slug: 'new-project', pricing: null },
];

export function ServicesPage() {
  const { data: services = [], isLoading } = useServices();
  const list = services.length > 0 ? services : FALLBACK;

  const location = useLocation();

  // Smooth-scroll to a service anchor when the URL hash points to one.
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.focus({ preventScroll: true });
      }
    }
  }, [location.hash]);

  const toc = useMemo(
    () => list.map((s) => ({ id: s.slug, label: s.title })),
    [list],
  );

  return (
    <>
      <Meta
        title="Services"
        description="Twelve capabilities Vonlof offers — from custom software and AI solutions to cloud infrastructure and UI/UX design."
        path="/services"
      />
      <main className="pt-24 pb-12 sm:pt-28 md:pt-32 md:pb-20">
        <MaskReveal className="container-wide" duration={1}>
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Services</p>
          </FadeIn>
          <TextSplit
            text="Capabilities that scale with you."
            className="mt-3 display-serif text-display-l text-fg"
            delay={0.1}
            stagger={0.06}
          />
          <FadeIn delay={0.35}>
            <p className="mt-6 max-w-2xl text-lg text-fg-muted">
              Twelve capabilities. One team. From the first sketch to production at scale — and every operational step in between.
            </p>
          </FadeIn>

          <div className="mt-10 grid gap-8 lg:mt-12 lg:gap-12 lg:grid-cols-12">
            {/* TOC (sticky on desktop, horizontal scroller on mobile) */}
            <aside className="lg:col-span-3">
              <nav aria-label="Services" className="lg:sticky lg:top-24">
                <p className="mb-3 hidden text-xs font-semibold uppercase tracking-wider text-fg-subtle lg:block">
                  On this page
                </p>
                <ul className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2 scrollbar-hide scroll-x-mask sm:mx-0 sm:px-0 lg:block lg:space-y-1.5 lg:overflow-visible lg:border-l lg:border-border lg:pb-0 lg:[mask-image:none] [mask-image:none]">
                  {toc.map((t) => (
                    <li key={t.id} className="shrink-0 lg:shrink lg:[&:first-child]:-mt-0">
                      <a
                        href={`#${t.id}`}
                        className="-ml-px block whitespace-nowrap rounded-full border border-border bg-bg-elevated px-3 py-1.5 text-xs text-fg-muted transition-colors hover:border-gold-500/40 hover:text-fg lg:whitespace-normal lg:rounded-none lg:border-l lg:border-transparent lg:bg-transparent lg:px-0 lg:py-1 lg:pl-4 lg:text-sm"
                      >
                        {t.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            {/* Service sections */}
            <div className="space-y-12 lg:col-span-9 lg:space-y-20">
              {isLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <Skeleton className="h-6 w-1/3" />
                      <Skeleton className="mt-3 h-12 w-3/4" />
                      <Skeleton className="mt-4 h-4 w-full" />
                    </div>
                  ))
                : list.map((service) => (
                    <ServiceSection key={service.id} service={service} />
                  ))}
            </div>
          </div>

          {/* CTA */}
          <FadeIn delay={0.2}>
            <div className="mt-16 rounded-2xl border border-gold-500/30 bg-bg-elevated/40 px-6 py-12 text-center sm:mt-24 sm:px-8 sm:py-16 md:px-16 md:py-24">
              <h2 className="display-serif text-display-l text-fg">Don't see what you need?</h2>
              <p className="mx-auto mt-4 max-w-xl text-fg-muted">
                We take on bespoke engagements too. Tell us what you're building.
              </p>
              <div className="mt-8">
                <Button asChild size="xl">
                  <Link to="/contact">
                    Start a conversation <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </FadeIn>
        </MaskReveal>
      </main>
    </>
  );
}

function ServiceSection({ service }: { service: Service }) {
  const IconName = (service.icon ?? 'Code2') as keyof typeof Icons;
  const Icon = (Icons[IconName] ?? Icons.Code2) as LucideIcon;
  return (
    <section
      id={service.slug}
      tabIndex={-1}
      className="rounded-xl border border-border bg-bg-elevated/30 p-5 focus:outline-none sm:p-8 md:p-12"
      style={{ scrollMarginTop: '5rem' }}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gold-500/10 text-gold-500 sm:h-12 sm:w-12">
          <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Service</p>
          <h2 className="mt-1 display-serif text-2xl text-fg sm:text-3xl md:text-4xl">{service.title}</h2>
        </div>
      </div>

      {service.summary && <p className="mt-5 max-w-2xl text-base text-fg-muted sm:mt-6 sm:text-lg">{service.summary}</p>}

      <div className="mt-6 grid gap-6 sm:mt-8 sm:gap-8 md:grid-cols-2">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">What you get</h3>
          {service.deliverables.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {service.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-2 text-sm text-fg">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold-500" />
                  {d}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-fg-subtle">No deliverables defined yet.</p>
          )}
        </div>
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">How we work</h3>
          {service.description_md ? (
            <p className="mt-3 text-sm leading-relaxed text-fg-muted">{service.description_md}</p>
          ) : (
            <p className="mt-3 text-sm text-fg-subtle">Add a description from the admin portal.</p>
          )}
        </div>
      </div>

      {service.tech_strip.length > 0 && (
        <div className="mt-6 sm:mt-8">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">Tech we use</h3>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {service.tech_strip.map((t) => (
              <Badge key={t} variant="outline">{t}</Badge>
            ))}
          </div>
        </div>
      )}

      {service.pricing && <VideoPricingBlock pricing={service.pricing} />}

      <div className="mt-6 sm:mt-8">
        <Button asChild>
          <Link to={service.cta_href}>
            {service.cta_label} <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </section>
  );
}